import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { InvoiceActionModal } from 'src/components/invoice/action-modal/InvoiceActionModal'
import { PaymentReleaseModal } from 'src/components/invoice/payment-release/PaymentReleaseModal'
import { ComposedInvoiceDetailsCard } from 'src/components/invoice/composed-invoice-details/ComposedInvoiceDetailsCard'
import { ComposedInvoiceStatusCell } from 'src/components/invoice/grid/ComposedInvoiceStatusCell'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import {
  AdminClearInvoiceActionRun,
  AdminFetchInvoices,
  AdminRunInvoiceAction,
} from 'src/constants/actionTypes'
import { invoicePageSizeOptions } from 'src/constants/invoice/constants'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type {
  InvoiceActionResult,
  PendingInvoiceAction,
  PendingPaymentReleaseAction,
} from 'src/interfaces/invoice'
import { buildCustomerTabPath } from 'src/pages/admin-dashboard/adminDashboardNav'
import {
  getComposedInvoices,
  getInvoiceActionRun,
  getInvoicesError,
  getInvoicesPagination,
} from 'src/selectors/invoice'
import { getComposedInvoiceLabel } from 'src/utils/invoiceLabels'

const INVOICE_TAB_SLUG = 'invoice'

/**
 * The widest page the list offers. A deep link lands here with nothing loaded, and the endpoint
 * has no by-id read or filter, so the best this page can do is load the largest first page and
 * look for the invoice in it.
 */
const DEEP_LINK_PAGE_SIZE = invoicePageSizeOptions[invoicePageSizeOptions.length - 1]

/**
 * A composed invoice's partner invoices as their own page, reached by clicking a row of the
 * Invoice tab — the same shape as an insurance policy opened from the Insurance tab. Derived
 * from the list in state, never fetched by id (there is no such endpoint): coming from the tab
 * the row is already there; on a deep link the first page is fetched and searched.
 */
const CustomerInvoiceDetailsPageComponent = () => {
  const { customerId, composedInvoiceId } = useParams<{
    customerId: string
    composedInvoiceId: string
  }>()
  const dispatch = useDispatch()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const { isAuthenticated } = useAuthSession()

  const composedInvoices = useSelector(getComposedInvoices)
  const pagination = useSelector(getInvoicesPagination)
  const invoicesError = useSelector(getInvoicesError)
  const actionRun = useSelector(getInvoiceActionRun)
  // Every invoice action posts to an `invoices_manage` route; without it the row menu is gone.
  const canManageInvoices = useHasPermission(InternalPermission.InvoicesManage)

  const [pendingAction, setPendingAction] = useState<PendingInvoiceAction | null>(null)
  const [pendingPaymentRelease, setPendingPaymentRelease] =
    useState<PendingPaymentReleaseAction | null>(null)

  const invoice = useMemo(() => {
    const found = composedInvoices.find((candidate) => {
      return candidate.id === composedInvoiceId
    })

    return found ?? null
  }, [composedInvoices, composedInvoiceId])

  // Whether the list has been (re)loaded for this page at least once. The slice has no
  // per-customer "loaded" marker, so "not found" is only claimed after our own fetch settled.
  const [hasFetchSettled, setHasFetchSettled] = useState<boolean>(false)

  /**
   * Refetches the window the invoice was found in — the same limit/offset the list answered
   * with — so an invoice opened from page 3 of the tab is still there after an action. On a
   * deep link nothing is loaded yet and the widest first page is used instead.
   */
  const fetchInvoices = useCallback(async () => {
    if (!customerId) {
      return
    }

    await asyncDispatchWithErrorToast(
      AdminFetchInvoices.request({
        partnerId: customerId,
        limit: pagination.limit ?? DEEP_LINK_PAGE_SIZE,
        offset: pagination.offset ?? 0,
      }),
    )
    setHasFetchSettled(true)
  }, [asyncDispatchWithErrorToast, customerId, pagination.limit, pagination.offset])

  // One fetch on arrival, and only when the row is not already in state — coming from the tab
  // it is, and refetching would flash the skeleton over data that is already correct.
  useEffect(() => {
    if (!isAuthenticated || !customerId || hasFetchSettled) {
      return
    }

    if (invoice) {
      setHasFetchSettled(true)

      return
    }

    fetchInvoices()
  }, [isAuthenticated, customerId, hasFetchSettled, invoice, fetchInvoices])

  const handleRunAction = useCallback((action: PendingInvoiceAction) => {
    setPendingAction(action)
  }, [])

  const handlePaymentReleaseAction = useCallback((action: PendingPaymentReleaseAction) => {
    setPendingPaymentRelease(action)
  }, [])

  // No refetch here: a success patches the row from the response in the reducer, and a
  // stale-row failure already refetched from the saga.
  const handleClosePaymentRelease = useCallback(() => {
    setPendingPaymentRelease(null)
  }, [])

  // Same contract as the Invoice tab: the saga resolves with per-invoice results even on a
  // partial failure, and the modal's results step reports them — no toast here.
  const handleConfirmAction = useCallback(
    async (invoiceIds: string[]) => {
      if (!pendingAction || !customerId) {
        return
      }

      await asyncDispatchWithErrorToast<InvoiceActionResult[]>(
        AdminRunInvoiceAction.request({
          partnerId: customerId,
          invoiceIds,
          verb: pendingAction.verb,
          composedInvoiceLabel: pendingAction.composedInvoiceLabel,
          invoiceLabels: pendingAction.invoiceLabels,
        }),
      )
    },
    [pendingAction, customerId, asyncDispatchWithErrorToast],
  )

  // Every POST returns void and the cascading state cannot be rebuilt client-side, so the list
  // is refetched after a run — but only after a run: dismissing the confirm step changes nothing.
  const handleCloseModal = useCallback(() => {
    const hadRun = actionRun !== null

    setPendingAction(null)
    dispatch(AdminClearInvoiceActionRun())

    if (!hadRun) {
      return
    }

    fetchInvoices()
  }, [actionRun, dispatch, fetchInvoices])

  const invoiceTabPath = customerId ? buildCustomerTabPath(customerId, INVOICE_TAB_SLUG) : undefined

  return (
    <Fragment>
      {/* A flex column so the table card takes exactly the height left under the header and
          scrolls inside itself — never the page behind it. */}
      <div
        data-testid="admin-customer-invoice-details-page"
        css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
      >
        {invoicesError ? (
          <Typography variant="body2" css={tw`mb-3 shrink-0 text-critical-500`}>
            {invoicesError}
          </Typography>
        ) : null}
        {!invoice && !hasFetchSettled ? (
          <SectionCardSkeleton fields={2} dataTestId="admin-customer-invoice-details-loading" />
        ) : null}
        {!invoice && hasFetchSettled ? (
          <div data-testid="admin-customer-invoice-details-not-found">
            <Typography variant="body2" css={tw`text-shades-500`}>
              This invoice could not be found. It may be on a later page of the list, or the link
              may be out of date.
            </Typography>
            {invoiceTabPath ? (
              <Link to={invoiceTabPath} data-testid="admin-customer-invoice-details-back-link">
                Back to Invoice
              </Link>
            ) : null}
          </div>
        ) : null}
        {invoice ? (
          <Fragment>
            <div css={tw`mb-6 flex shrink-0 flex-row flex-wrap items-center gap-3`}>
              <Typography variant="h2" css={tw`min-w-0 break-all`}>
                {getComposedInvoiceLabel(invoice)}
              </Typography>
              <ComposedInvoiceStatusCell invoice={invoice} />
            </div>
            <ComposedInvoiceDetailsCard
              invoice={invoice}
              canManage={canManageInvoices}
              onRunAction={handleRunAction}
              onPaymentReleaseAction={handlePaymentReleaseAction}
            />
          </Fragment>
        ) : null}
      </div>
      <InvoiceActionModal
        pendingAction={pendingAction}
        onConfirm={handleConfirmAction}
        onClose={handleCloseModal}
      />
      <PaymentReleaseModal
        partnerId={customerId}
        pendingAction={pendingPaymentRelease}
        onClose={handleClosePaymentRelease}
      />
    </Fragment>
  )
}

export const CustomerInvoiceDetailsPage = memo(CustomerInvoiceDetailsPageComponent)
